import React, { useContext, useEffect, useState } from 'react';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import SecondaryButton from "../../ui/SecondaryButton";
import { ApplicableTableSettings } from "../../tableSettings";
import SmartTable from "../../uiControls/SmartTable";
import { myMemoTable } from "./tableSettings";
import { TaskReminder, TaskReminderList } from '../../fms.core/TaskReminder';
import ApplicationContext from '../../ApplicationContext';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { DateTimeRenderer, NumberedIndicatorRenderer, TextAlignLeftRenderer } from '../../uiControls/SmartTableRenderers';
import IfmsModal from '../../ui/IfmsModal';
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import TextBox from '../../ui/TextBox';
import LeftRightSplit from '../../ui/LeftRightSplit';
import MultilineTextBox from '../../ui/MultilineTextBox';
import { ListResult } from '../../framework.core/data/ListResult';
import Number from '../../ui/Number';
import PrimaryInverseButton from '../../ui/PrimaryInverseButton';
import { compareIso, now } from '../../dateUtils';
import _ from 'lodash';
import useDirtyCheck from '../../hooks/useDirtyCheck2';


interface MyMemoStorageKeys {
  modelStorage: string,
};

interface MyMemoParams {
  onClickEdit?: (tbName:string, tbId:number, acctTbName: string) => void,
  showMainPop: boolean,
  setShowMainPop: React.Dispatch<React.SetStateAction<boolean>>
};

interface MyMemoLoadedParams {
  taskReminder: TaskReminder[],
  onClickEdit?: (tbName:string, tbId:number, acctTbName: string) => void,
  showMainPop: boolean,
  setShowMainPop: React.Dispatch<React.SetStateAction<boolean>>,
  reload : (x : boolean) =>  Promise<void>
};

interface EditRendererParam {
  data: TaskReminder[],
  row: any,
  cell: any,
}

function getMyMemoStorageKeys(): MyMemoStorageKeys {
  return { modelStorage: 'reminder-memo-view' }
}

function MyMemoImpl(params: MyMemoParams) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const { modelStorage } = getMyMemoStorageKeys();
  const { setShowMainPop, showMainPop } = params;

  const [inStorage, setInStorage, , ] = useTypedFastStorage<TaskReminderList>(modelStorage, null);
  const { setCleanState} = useDirtyCheck(modelStorage, ['remindDate']);
  
  useEffect(() => {
    initialize();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  async function loadExisting() {
    const apiResult: ListResult | null = await appContext.api.getTaskReminder(true, appContext.user ? appContext.user.id ?? '' : '');
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      return [];
    }
    return apiResult?.entities ?? [];
  }

  async function initialize() {
    const data = await loadExisting();
    setCleanState(data);
    setInStorage({taskReminderList : data})
  }

  async function reload(showAll: boolean = true) {
    setCleanState(_.cloneDeep(inStorage ?? []));
    const apiResult: ListResult | null = await appContext.api.getTaskReminder(showAll, appContext.user ? appContext.user.id ?? '' : '');
    if (!apiResult || !apiResult.success) {
      return;
    }
    const data: TaskReminder[] = apiResult?.entities;
    setCleanState(data);

    if (showAll) {
      setInStorage({taskReminderList : _.cloneDeep(data)});
    }
    else {
      const dueData: TaskReminder[] = data.filter((x: TaskReminder) => (compareIso(x.dueDate, now()) < 0));
      setInStorage({taskReminderList : _.cloneDeep(dueData)})
    }
  }

  if (!inStorage) {
    return null;
  }

  return (
    <MyMemoInitializedImpl
      taskReminder={inStorage.taskReminderList}
      onClickEdit={params.onClickEdit}
      showMainPop={showMainPop}
      reload = {reload}
      setShowMainPop={setShowMainPop}
    />
  );
}

function MyMemoInitializedImpl({
  taskReminder,
  onClickEdit,
  showMainPop,
  setShowMainPop,
  reload
}: MyMemoLoadedParams) {
  const appContext = useContext(ApplicationContext);
  const [selectedRow, setSelectedRow] = useState(0);
  const [snooze, setSnooze] = useState(false);
  const [showAll, setshowAll] = useState(true);
  let enterTime = 30;
  const tableOptions: ApplicableTableSettings = {
    ...myMemoTable,
    selectable: true,
    height: '160px',
    selectedRowIndex: selectedRow,
    tableName: 'MyMemoTable',
    rowProps: (row: any) => ({ onClick: () => setSelectedRow(row.index) }),
    data: taskReminder.map(x => ({ ...x })),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'editParam', Cell: EditRenderer },
      { accessor: 'remindDate', Cell: DateTimeRenderer },
      { accessor: 'dueDate', Cell: DateTimeRenderer },
      { accessor: 'opType', Cell: TextAlignLeftRenderer },
      { accessor: 'refNo', Cell: TextAlignLeftRenderer },
      { accessor: 'mTitle', Cell: TextAlignLeftRenderer },
      { accessor: 'mU1ID', Cell: TextAlignLeftRenderer },
      { accessor: 'mU1Date', Cell: DateTimeRenderer },
    ],
  };

  function EditRenderer({ data, row, cell }: EditRendererParam) {
    return <PrimaryInverseButton onClick={() => {
      if (onClickEdit)
        onClickEdit(data[row.index].mTbName, data[row.index].mTbId, data[row.index].acctTbName);
      appContext.setAppInfoAreaContent('memo');
      appContext.setMyMemoTaskReminder(true);
      appContext.setMyMemoId(data[row.index].mId??0);
      setShowMainPop(false);
    }} style={{ width: '100%', color: 'blue', fontWeight: 'bold', fontSize: '10px', padding: '0 !important' }}>Edit</PrimaryInverseButton>
  }

  function close() {
    setShowMainPop(false);
    appContext.setMyMemoTaskReminder(false);
    appContext.setAppInfoAreaContent(appContext.appInfoAreaContent);
  }

  return (
    <IfmsModal
      isBlueHeader
      cancelOnEsc
      title={'Task Reminder List'}
      isOpen={showMainPop}
      onCancel={() => { close()}}
      draggable
      width={`${1000}px`}>
      {
        <div className='report-popup' style={{ marginBottom: '-20px' }}>
          <div style={{ width: '400px', marginBottom: '10px' }}>
            <InlineInputControlRow
              smallMargin
              mode="w60"
              label="User : "
            >
              <TextBox value={appContext.user ? appContext.user.id ?? '' : ''} disabled dark />
            </InlineInputControlRow>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <SmartTable {...tableOptions} />
          </div>
          <div style={{ display: 'flex' }}>
            <LeftRightSplit
              leftSize="calc(100% - 300px)"
              rightSize="300px"
              left={
                <TextBox value={taskReminder[selectedRow]?.mTitle ?? ''} disabled dark />
              }
              right={
                <div style={{ paddingLeft: '50px' }}>
                  <TextBox value={taskReminder[selectedRow]?.refNo ?? ''} disabled dark />
                </div>
              }
            />
          </div>
          <div className="clearfix" />
          <div style={{ marginTop: '3px' }}>
            <MultilineTextBox
              maxLength={200}
              disabled={true}
              style={{ height: '150px' }}
              value={taskReminder[selectedRow]?.mContents ?? ''}
              onChange={() => { }} />
          </div>
          <div className='report-footer-3buttons'>
            <SecondaryButton
              focusable={true}
              size="regular"
              style={{ width: '150px' }}
              onClick={async () => {
                setshowAll(!showAll);
                await reload(!showAll);
                setSelectedRow(0);
               }}>
                {showAll ? 'Show Over Due Only' : 'Show All'}
            </SecondaryButton>
            <SecondaryButton
              focusable={true}
              size="regular"
              style={{ width: '90px' }}
              onClick={() => { setSnooze(true) }}>
              Snooze
            </SecondaryButton>
            <SecondaryButton
              focusable={true}
              size="regular"
              style={{ width: '90px' }}
              onClick={() => {close()}}>
              Exit
            </SecondaryButton>
          </div>
        </div>
      }
      {snooze &&
        <IfmsModal
          isBlueHeader
          cancelOnEsc
          onCancel={() => { setSnooze(false) }}
          draggable
          isOpen={true}
          width={`${300}px`}
          title={'Snooze Reminder'}>
          {
            <div className='report-popup' style={{ marginBottom: '-20px' }}>
              <div style={{ width: '200px' }}>
                <InlineInputControlRow
                  smallMargin
                  mode="smaller"
                  label="Enter Minutes : "
                >
                  <Number
                    value={enterTime}
                    onChange={e => { enterTime = e ?? 0 }}
                    max={999}
                    min={0}
                  />
                </InlineInputControlRow>
              </div>
              <div className='report-footer-2buttons'>
                <SecondaryButton
                  focusable={true}
                  size="regular"
                  style={{ width: '60px' }}
                  onClick={async () => {
                    //const apiResult = 
                    await appContext.api.saveMinutes(enterTime.toString(), appContext.user ? appContext.user.id ?? '' : '');
                    enterTime = 30;
                    setSnooze(false);
                    setShowMainPop(false);
                    appContext.setMyMemoTaskReminder(false);
                    appContext.setUseCheckReminder(!appContext.useCheckReminder);
                  }}>
                  OK
                </SecondaryButton>
                <SecondaryButton
                  focusable={true}
                  size="regular"
                  style={{ width: '60px' }}
                  onClick={() => { setSnooze(false) }}>
                  Cancel
                </SecondaryButton>
              </div>
            </div>
          }
        </IfmsModal>
      }
    </IfmsModal>

  );
}

const MyMemoDetails = ({
  onClickEdit,
  setShowMainPop,
  showMainPop
}: { onClickEdit?: (tbName:string, tbId:number, acctTbName: string) => void,  setShowMainPop: React.Dispatch<React.SetStateAction<boolean>>, showMainPop: boolean }) => {
  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <MyMemoImpl setShowMainPop={setShowMainPop} showMainPop={showMainPop} onClickEdit={onClickEdit} key={`MyMemo-details`} />
  );
};
export default MyMemoDetails;
